import React, { useMemo, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button, Checkbox, Modal, Select, Table, TableColumn } from 'common/components';
import {
    getOrphanNumbers,
    releaseOrphanNumbers,
    OrphanNumber,
    OrphanNumbersResponse,
    ReleaseOrphanNumbersResponse,
} from 'services/api/PhoneNumberAPI';
import { Tenant } from 'types/accessManager.type';
import styles from '../PhoneNumberManager.module.scss';

// The release endpoint accepts at most this many numbers per request.
export const MAX_RELEASE_BATCH = 100;

interface OrphanNumbersTabProps {
    tenants: Tenant[];
    isAccessManagerLoading: boolean;
}

const PROVIDER_LABELS: Record<string, string> = {
    twilio: 'Twilio',
    telnyx: 'Telnyx',
};

const pluralizeNumbers = (count: number): string => `${count} number${count === 1 ? '' : 's'}`;

const getErrorMessage = (error: unknown, fallback: string): string => {
    const err = error as { response?: { data?: { error?: string; message?: string } }; message?: string };
    return err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
};

const showReleaseResultToasts = (result: ReleaseOrphanNumbersResponse) => {
    const released = result.releasedNumbers ?? [];
    const failed = result.failedToReleaseNumbers ?? [];
    const skipped = result.skippedNumbers ?? [];

    if (released.length > 0) {
        toast.success(`Released ${pluralizeNumbers(released.length)}.`);
    }

    const warningParts: string[] = [];
    if (failed.length > 0) {
        warningParts.push(`Failed to release from the carrier: ${failed.join(', ')}.`);
    }
    if (skipped.length > 0) {
        warningParts.push(`No longer orphaned, skipped: ${skipped.join(', ')}.`);
    }
    if (result.warnings) {
        warningParts.push(result.warnings);
    }
    if (warningParts.length > 0) {
        toast.warning(warningParts.join(' '));
    }
};

const OrphanNumbersTab: React.FC<OrphanNumbersTabProps> = ({ tenants, isAccessManagerLoading }) => {
    const [tenantId, setTenantId] = useState('');
    const [scanResult, setScanResult] = useState<OrphanNumbersResponse | null>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [selectedNumbers, setSelectedNumbers] = useState<string[]>([]);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isReleasing, setIsReleasing] = useState(false);
    // Tracks the latest scan so a slow response for a previous tenant is ignored.
    const scanRequestId = useRef(0);

    const orphans = useMemo(() => scanResult?.orphans ?? [], [scanResult]);
    const allNumbers = useMemo(() => orphans.map((orphan) => orphan.phoneNumber), [orphans]);
    const areAllSelected = allNumbers.length > 0 && allNumbers.every((number) => selectedNumbers.includes(number));
    const areSomeSelected = !areAllSelected && allNumbers.some((number) => selectedNumbers.includes(number));
    const isOverBatchLimit = selectedNumbers.length > MAX_RELEASE_BATCH;

    const scan = async (scanTenantId: string) => {
        const requestId = ++scanRequestId.current;
        setIsScanning(true);
        setSelectedNumbers([]);
        try {
            const result = await getOrphanNumbers(scanTenantId);
            if (requestId !== scanRequestId.current) return;
            setScanResult(result);
        } catch (error) {
            if (requestId !== scanRequestId.current) return;
            setScanResult(null);
            toast.error(getErrorMessage(error, 'Failed to scan for orphan numbers. Please try again.'));
        } finally {
            if (requestId === scanRequestId.current) {
                setIsScanning(false);
            }
        }
    };

    const handleTenantChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        // Invalidate any scan still in flight for the previous tenant.
        scanRequestId.current += 1;
        setTenantId(event.target.value);
        setScanResult(null);
        setSelectedNumbers([]);
        setIsScanning(false);
    };

    const closeConfirm = () => {
        if (isReleasing) return;
        setIsConfirmOpen(false);
    };

    const handleConfirmRelease = async () => {
        if (!tenantId || selectedNumbers.length === 0 || isOverBatchLimit) return;
        setIsReleasing(true);
        let released = false;
        try {
            const result = await releaseOrphanNumbers({ tenantId, phoneNumbers: selectedNumbers });
            showReleaseResultToasts(result);
            released = true;
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to release orphan numbers. Please try again.'));
        } finally {
            setIsReleasing(false);
            setIsConfirmOpen(false);
        }
        if (released) {
            await scan(tenantId);
        }
    };

    const columns: TableColumn<OrphanNumber>[] = useMemo(() => {
        const toggleRow = (number: string) =>
            setSelectedNumbers((prev) =>
                prev.includes(number) ? prev.filter((selected) => selected !== number) : [...prev, number]
            );

        return [
            {
                key: 'checkbox',
                title: (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            checked={areAllSelected}
                            indeterminate={areSomeSelected}
                            onChange={() => setSelectedNumbers(areAllSelected ? [] : allNumbers)}
                            disabled={allNumbers.length === 0 || isReleasing}
                            onClick={(e) => e.stopPropagation()}
                            aria-label='Select all orphan numbers'
                        />
                    </div>
                ),
                width: 40,
                align: 'center',
                render: (_, record) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            checked={selectedNumbers.includes(record.phoneNumber)}
                            onChange={() => toggleRow(record.phoneNumber)}
                            disabled={isReleasing}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select ${record.phoneNumber}`}
                        />
                    </div>
                ),
            },
            {
                key: 'phoneNumber',
                title: 'Phone Number',
                dataIndex: 'phoneNumber',
                width: 180,
            },
            {
                key: 'providerSid',
                title: 'Provider SID',
                dataIndex: 'providerSid',
            },
            {
                key: 'providerTrunkId',
                title: 'Trunk / Connection ID',
                dataIndex: 'providerTrunkId',
            },
        ];
    }, [areAllSelected, areSomeSelected, allNumbers, selectedNumbers, isReleasing]);

    const providerLabel = scanResult ? (PROVIDER_LABELS[scanResult.provider] ?? scanResult.provider) : '';
    const selectedCount = selectedNumbers.length;

    return (
        <div className={styles.spamContainer} data-testid='orphan-numbers-tab'>
            <div className={styles.formSection}>
                <h2 className={styles.sectionTitle}>Orphan Numbers</h2>
                <p className={styles.description}>
                    An orphan number is a number on the tenant&apos;s carrier account (Twilio or Telnyx) that has no
                    active record in SLAI. Scan a tenant to find them, then release the ones you no longer need.{' '}
                    <strong>Releasing a number is permanent.</strong>
                </p>

                <div className={styles.formGroup}>
                    <Select
                        label='Tenant *'
                        name='tenantId'
                        value={tenantId}
                        onChange={handleTenantChange}
                        options={[
                            { value: '', label: 'Select a Tenant', disabled: true },
                            ...(tenants || []).map((tenant) => ({
                                value: tenant.id,
                                label: tenant.name,
                            })),
                        ]}
                        disabled={isAccessManagerLoading || isReleasing}
                    />
                </div>

                <div className={styles.buttonContainer}>
                    <Button
                        type='button'
                        variant='primary'
                        onClick={() => scan(tenantId)}
                        disabled={!tenantId || isScanning || isReleasing || isAccessManagerLoading}
                        isLoading={isScanning}
                    >
                        {isScanning ? 'Scanning...' : 'Scan'}
                    </Button>
                </div>
            </div>

            {scanResult && (
                <div className={styles.formSection}>
                    <p className={styles.description} data-testid='orphan-numbers-summary'>
                        <strong>{providerLabel}</strong>: {scanResult.count} orphan
                        {scanResult.count === 1 ? '' : 's'} out of {scanResult.providerNumberCount} carrier number
                        {scanResult.providerNumberCount === 1 ? '' : 's'}
                    </p>

                    <Table<OrphanNumber>
                        data={orphans}
                        columns={columns}
                        emptyText='No orphan numbers found.'
                        rowKey={(record) => record.phoneNumber}
                        showHeader
                        variant='secondary'
                        hoverable
                        size='medium'
                        isLoading={isScanning}
                    />

                    {isOverBatchLimit && (
                        <p className={styles.errorText} data-testid='orphan-numbers-batch-hint'>
                            You can release at most {MAX_RELEASE_BATCH} numbers at a time. Deselect{' '}
                            {selectedCount - MAX_RELEASE_BATCH} to continue.
                        </p>
                    )}

                    <div className={styles.buttonContainer}>
                        <Button
                            type='button'
                            variant='primary'
                            icon={Trash2}
                            onClick={() => setIsConfirmOpen(true)}
                            disabled={selectedCount === 0 || isOverBatchLimit || isReleasing || isScanning}
                        >
                            {selectedCount > 0 ? `Release selected (${selectedCount})` : 'Release selected'}
                        </Button>
                    </div>
                </div>
            )}

            <Modal
                isOpen={isConfirmOpen}
                onClose={closeConfirm}
                title='Release orphan numbers'
                size='sm'
                showDefaultFooter={false}
                showCloseButton={!isReleasing}
                closeOnOverlayClick={!isReleasing}
                closeOnEscape={!isReleasing}
                footer={
                    <div className={styles.modalFooter}>
                        <Button variant='outline-secondary' onClick={closeConfirm} disabled={isReleasing}>
                            Cancel
                        </Button>
                        <Button
                            variant='primary'
                            icon={Trash2}
                            onClick={handleConfirmRelease}
                            isLoading={isReleasing}
                            disabled={isReleasing}
                            data-testid='confirm-release-orphan-numbers'
                        >
                            Release
                        </Button>
                    </div>
                }
            >
                <p className={styles.modalMessage} data-testid='release-orphan-numbers-confirm-message'>
                    This permanently releases {pluralizeNumbers(selectedCount)} from the {providerLabel || 'carrier'}{' '}
                    account. Any calls in progress on {selectedCount === 1 ? 'it' : 'them'} will drop. This cannot be
                    undone.
                </p>
            </Modal>
        </div>
    );
};

export default OrphanNumbersTab;
